import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLeaf, 
  faClock, 
  faMapMarkerAlt, 
  faExternalLinkAlt,
  faShoppingBag,
  faUtensils,
  faCrown,
  faTree,
  faPaw,
  faPhone,
  faCalendarAlt,
  faTimes,
  faInfoCircle,
  faCheck,
  faBook,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import ParallaxBanner from '../../common/Parralax-banner';
import Footer from '../../Footer/Footer';
import { useRetailSection } from '../../../hooks/useRetailSection';
import Button from '../../../components/Button/Button';

// Import images (replace with actual shop images)
import farmShopImage from '../../../assets/farmshop1.webp';
import duchessImage from '../../../assets/retail1.webp';
import outdoorLivingImage from '../../../assets/outdoor-living.jpg';
import belvoirBooksImage from '../../../assets/books/thousand-years.png';
import retailBanner from '../../../assets/banner/retailBan3.webp';


const ShopsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const CurrentTimeDisplay = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 3rem;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const IntroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto 2rem;
  color: ${props => props.theme.colors.secondary};
`;

const FeaturedShops = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-bottom: 6rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const OtherShops = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ShopCard = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const FeaturedShopCard = styled(ShopCard)`
  display: flex;
  flex-direction: column;
`;

const ShopImage = styled.div`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const FeaturedShopImage = styled(ShopImage)`
  height: 250px;
`;

const ShopContent = styled.div`
  padding: 1.5rem;
`;

const ShopTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ShopDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.secondary};
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: ${props => {
    if (props.isOpen) return 'rgba(76, 175, 80, 0.85)';
    if (props.openingSoon) return 'rgba(255, 165, 0, 0.85)';
    return 'rgba(244, 67, 54, 0.85)';
  }};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(4px);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  
  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const PhoneLink = styled.a`
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  button {
    background-color: ${props => props.theme.colors.accent};
    color: white;
    border: 2px solid transparent;
    border-radius: 50px;
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: transparent;
      color: ${props => props.theme.colors.accent};
      border-color: ${props => props.theme.colors.accent};
      transform: translateY(-2px);
    }
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const UpcomingDatesSection = styled.div`
  margin: 4rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const UpcomingDatesTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const DatesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const DateCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid ${props => {
    if (props.$type === 'christmas') return 'rgba(220, 20, 60, 0.8)';
    if (props.$type === 'closed') return 'rgba(244, 67, 54, 0.8)';
    if (props.$type === 'special') return 'rgba(255, 165, 0, 0.8)';
    return 'rgba(34, 139, 34, 0.8)';
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const DateTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateRange = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.8rem;
`;

const DateDetails = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const DateNote = styled.div`
  font-size: 0.9rem;
  font-style: italic;
  color: ${props => props.theme.colors.secondary};
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => props.theme.colors.greyBackground};
`;

const ShopsOverview = () => {
  const navigate = useNavigate();
  useRetailSection();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const isShopOpen = (openTime, closeTime, currentTime, closedOn = [], shopId = '') => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    
    // Check if today is a closed day
    if (closedOn.includes(currentTime.getDay())) {
      return { isOpen: false, openingSoon: false };
    }
    
    // Christmas Day - All shops closed
    if (year === 2025 && month === 11 && day === 25) {
      return { isOpen: false, openingSoon: false };
    }
    
    // Boxing Day (Dec 26, 2025) - Farm Shop and Duchess Collection: 9am-3:30pm
    if (year === 2025 && month === 11 && day === 26) {
      if (shopId === 'farm-shop' || shopId === 'duchess-collection') {
        const currentTimeNum = currentTime.getHours() * 100 + currentTime.getMinutes();
        const openTimeNum = 9 * 100; // 9:00
        const closeTimeNum = 15 * 100 + 30; // 15:30
        const isOpen = currentTimeNum >= openTimeNum && currentTimeNum < closeTimeNum;
        const oneHourBeforeOpen = openTimeNum - 100;
        const openingSoon = currentTimeNum >= oneHourBeforeOpen && currentTimeNum < openTimeNum;
        return { isOpen, openingSoon };
      }
    }
    
    // New Year's Day (Jan 1, 2026) - Farm Shop and Duchess Collection: 9am-3:30pm
    if (year === 2026 && month === 0 && day === 1) {
      if (shopId === 'farm-shop' || shopId === 'duchess-collection') {
        const currentTimeNum = currentTime.getHours() * 100 + currentTime.getMinutes();
        const openTimeNum = 9 * 100; // 9:00
        const closeTimeNum = 15 * 100 + 30; // 15:30
        const isOpen = currentTimeNum >= openTimeNum && currentTimeNum < closeTimeNum;
        const oneHourBeforeOpen = openTimeNum - 100;
        const openingSoon = currentTimeNum >= oneHourBeforeOpen && currentTimeNum < openTimeNum;
        return { isOpen, openingSoon };
      }
    }
    
    const [openHour, openMinute] = openTime.split(':').map(Number);
    const [closeHour, closeMinute] = closeTime.split(':').map(Number);
    const currentTimeNum = currentTime.getHours() * 100 + currentTime.getMinutes();
    const openTimeNum = openHour * 100 + openMinute;
    const closeTimeNum = closeHour * 100 + closeMinute;
    const isOpen = currentTimeNum >= openTimeNum && currentTimeNum < closeTimeNum;
    const oneHourBeforeOpen = openTimeNum - 100;
    const openingSoon = currentTimeNum >= oneHourBeforeOpen && currentTimeNum < openTimeNum;
    return { isOpen, openingSoon };
  };

  const featuredShops = [
    {
      id: 'farm-shop',
      title: 'Farm Shop',
      icon: faLeaf,
      image: farmShopImage,
      description: 'Experience the finest local produce direct from our estate and carefully selected suppliers. Our Farm Shop offers fresh seasonal vegetables, artisanal cheeses, local meats, and specialty foods.',
      openTime: '09:00',
      closeTime: '17:00',
      path: '/retail-village/shops/farm-shop',
      comingSoon: 'Online shop launching May 2025',
      closedOn: [] // No closed days for Farm Shop
    },
    {
      id: 'duchess-collection',
      title: 'The Duchess Collection',
      icon: faCrown,
      image: duchessImage,
      description: 'Discover our exclusive collection of elegant fashion and homeware inspired by the rich heritage of Belvoir Castle. Each piece is carefully curated by the Duchess of Rutland, featuring designs drawn from the castle\'s historic art collection and architectural details.',
      openTime: '09:00',
      closeTime: '17:00',
      path: '/retail-village/shops/duchess-collection',
      comingSoon: 'Online boutique launching May 2025',
      closedOn: [] // No closed days for Duchess Collection
    }
  ];

  const otherShops = [
    {
      id: 'belvoir-pet-shop',
      title: 'Belvoir Pet Shop',
      icon: faPaw,
      image: 'https://BelvoirCastle.b-cdn.net/DogShop/Screenshot%202026-08-25%20at%2013.24.14.webp',
      description: 'A boutique dedicated entirely to dogs at Belvoir Retail Village — premium collars, treats, toys and apparel.',
      openTime: '09:00',
      closeTime: '16:00',
      path: '/retail-village/shops/belvoir-pet-shop',
      closedOn: []
    },
    {
      id: 'outdoor-living',
      title: 'Outdoor Living Collective',
      icon: faTree,
      image: outdoorLivingImage,
      description: 'Discover our curated collection of outdoor furniture, garden accessories, and lifestyle products for your outdoor spaces.',
      openTime: '09:00',
      closeTime: '17:00',
      path: '/retail-village/shops/outdoor-living-collective',
      closedOn: [] // No closed days for Outdoor Living Collective
    },
    {
      id: 'belvoir-books',
      title: 'Belvoir Books',
      icon: faBook,
      image: belvoirBooksImage,
      description:
        'Heritage titles from and about Belvoir—memoir, history, landscape, and art. Browse the collection and order for delivery.',
      onlineOnly: true,
      path: '/about/belvoir-books',
      buttonText: 'View books',
      closedOn: []
    }
  ];

  return (
    <ShopsContainer>
      <ParallaxBanner 
        title="Retail Village at Belvoir Castle"
        subtitle="A unique shopping destination in historic surroundings"
        image={retailBanner}
      />
      
      <ContentContainer>
        <IntroSection>
          <IntroTitle>Our Shops</IntroTitle>
          <IntroText>
            Discover a carefully curated selection of shops offering everything from fresh local produce 
            to luxury fashion, homewares, and garden essentials.
          </IntroText>
        </IntroSection>

        <CurrentTimeDisplay>
          Current Time: {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </CurrentTimeDisplay>

        <FeaturedShops>
          {featuredShops.map(shop => (
            <FeaturedShopCard key={shop.id}>
              <FeaturedShopImage image={shop.image}>
                <StatusBadge isOpen={isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen} openingSoon={isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon}>
                  <FontAwesomeIcon icon={faClock} />
                  {isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen ? 'Open Now' : isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon ? 'Opening Soon' : 'Closed'}
                </StatusBadge>
              </FeaturedShopImage>
              <ShopContent>
                <ShopTitle>
                  <FontAwesomeIcon icon={shop.icon} />
                  {shop.title}
                </ShopTitle>
                <ShopDescription>{shop.description}</ShopDescription>
                <InfoItem>
                  <FontAwesomeIcon icon={faClock} />
                  {shop.openTime} - {shop.closeTime}
                </InfoItem>
                {shop.comingSoon && (
                  <InfoItem style={{color: props => props.theme.colors.accent, marginTop: '1rem'}}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    {shop.comingSoon}
                  </InfoItem>
                )}
                <ButtonContainer>
                  <Button onClick={() => navigate(shop.path)}>{shop.buttonText || 'Visit Shop Page'}</Button>
                </ButtonContainer>
              </ShopContent>
            </FeaturedShopCard>
          ))}
        </FeaturedShops>

        <OtherShops>
          {otherShops.map(shop => (
            <ShopCard key={shop.id}>
              <ShopImage image={shop.image}>
                <StatusBadge
                  isOpen={shop.onlineOnly || (!shop.comingSoon && isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen)}
                  openingSoon={!shop.onlineOnly && !shop.comingSoon && isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon}
                >
                  <FontAwesomeIcon icon={shop.onlineOnly ? faGlobe : faClock} />
                  {shop.onlineOnly
                    ? 'Online only'
                    : shop.comingSoon
                      ? 'Coming Soon'
                      : isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).isOpen
                        ? 'Open Now'
                        : isShopOpen(shop.openTime, shop.closeTime, currentTime, shop.closedOn, shop.id).openingSoon
                          ? 'Opening Soon'
                          : 'Closed'}
                </StatusBadge>
              </ShopImage>
              <ShopContent>
                <ShopTitle>
                  <FontAwesomeIcon icon={shop.icon} />
                  {shop.title}
                </ShopTitle>
                <ShopDescription>{shop.description}</ShopDescription>
                <InfoItem>
                  <FontAwesomeIcon icon={shop.onlineOnly ? faGlobe : faClock} />
                  {shop.onlineOnly
                    ? 'Online shop only — order for delivery'
                    : `${shop.openTime} - ${shop.closeTime}`}
                </InfoItem>
                {shop.comingSoon && (
                  <InfoItem style={{color: '#d4af37', marginTop: '1rem'}}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    {shop.comingSoon}
                  </InfoItem>
                )}
                <ButtonContainer>
                  <Button onClick={() => navigate(shop.path)}>{shop.buttonText || 'Visit Shop Page'}</Button>
                </ButtonContainer>
              </ShopContent>
            </ShopCard>
          ))}
        </OtherShops>
        
        {/* <UpcomingDatesSection>
          <UpcomingDatesTitle>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Upcoming Important Dates for Shops
          </UpcomingDatesTitle>
          <DatesList>
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Normal Opening Hours
              </DateTitle>
              <DateRange>Daily (except special dates below)</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 5:00pm daily<br/>
                <strong>Other Retail Shops:</strong> Check individual shop pages for hours
              </DateDetails>
            </DateCard>
            
            <DateCard $type="closed">
              <DateTitle>
                <FontAwesomeIcon icon={faTimes} />
                Christmas Day
              </DateTitle>
              <DateRange>Thursday 25th December 2025</DateRange>
              <DateDetails>
                <strong>All shops closed</strong>
              </DateDetails>
              <DateNote>We will be closed for Christmas Day</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Boxing Day
              </DateTitle>
              <DateRange>Friday 26th December 2025</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 3:30pm<br/>
                <strong>Other shops:</strong> Closed
              </DateDetails>
              <DateNote>Special Boxing Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                New Year's Day
              </DateTitle>
              <DateRange>Thursday 1st January 2026</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 3:30pm<br/>
                <strong>Other shops:</strong> Closed
              </DateDetails>
              <DateNote>Special New Year's Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faCheck} />
                January - March 2026
              </DateTitle>
              <DateRange>1st January - 14th March 2026</DateRange>
              <DateDetails>
                <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 5:00pm daily<br/>
                <strong>Retail Village:</strong> 9:00am - 5:00pm<br/>
                <strong>Other shops:</strong> Check individual shop pages
              </DateDetails>
              <DateNote>Normal opening hours resume after New Year's Day</DateNote>
            </DateCard>
          </DatesList>
        </UpcomingDatesSection> */}
      </ContentContainer>
      
      <Footer />
    </ShopsContainer>
  );
};

export default ShopsOverview; 